/**
 * The sort orders a shopper can pick on browse and search pages.
 *
 * Carried in the URL as `?sortBy=`, so a sorted page can be shared, survives a
 * reload and the back button, and needs no state of its own: the browse and
 * search pages already read every URL param into the search they run.
 *
 * No value means "Best match", the ranking the page has always used.
 */

// No "Oldest first": nobody shops oldest-first. "Distance" took its place
// (utils/distance-sort.ts measures from the buyer's IP when the page sends no point).
export const LISTING_SORTS = ['date_desc', 'distance_asc', 'price_asc', 'price_desc'] as const;

export type ListingSort = (typeof LISTING_SORTS)[number];

export const SORT_OPTIONS: ReadonlyArray<{ value: ListingSort | ''; label: string }> = [
  { value: '', label: 'Best match' },
  { value: 'date_desc', label: 'Newest first' },
  { value: 'distance_asc', label: 'Distance: nearest first' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
];

/**
 * The default order's label. "Best match" only means something when there is
 * text to match; on home or a category page it is the ranking, so it is
 * "Recommended" there.
 */
export function defaultSortLabel(q: unknown): string {
  const text = Array.isArray(q) ? q[0] : q;
  return typeof text === 'string' && text.trim().length > 0 ? 'Best match' : 'Recommended';
}

const SORT_HEADINGS: Record<ListingSort, string> = {
  date_desc: 'Newest',
  distance_asc: 'Closest',
  price_asc: 'Price: Low to High',
  price_desc: 'Price: High to Low',
};

/**
 * The heading over a list, named for the order it is in. It said "Newest"
 * whatever the sort, so a price-sorted home page claimed to be newest first.
 */
export function sortHeading(sortBy: unknown, q?: unknown): string {
  const sort = parseListingSort(sortBy);
  return sort ? SORT_HEADINGS[sort] : defaultSortLabel(q);
}

/**
 * A browse page's heading, worded for its sort: "Browse Closest Fashion",
 * "Browse Fashion: Priced - Lowest". `verb` is the page's own lead word
 * ("Browse", "Shop"); a tag page has none. No sort is the plain heading.
 */
export function sortedPageHeading(name: string, sortBy: unknown, verb?: string): string {
  const lead = verb ? `${verb} ` : '';
  switch (parseListingSort(sortBy)) {
    case 'date_desc':
      return `${lead}Newest ${name}`;
    case 'distance_asc':
      return `${lead}Closest ${name}`;
    case 'price_asc':
      return `${lead}${name}: Priced - Lowest`;
    case 'price_desc':
      return `${lead}${name}: Priced - Highest`;
    default:
      return `${lead}${name}`;
  }
}

/** A shopper-pickable sort from a URL value, or undefined for anything else. */
export function parseListingSort(raw: unknown): ListingSort | undefined {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return (LISTING_SORTS as readonly unknown[]).includes(value) ? (value as ListingSort) : undefined;
}

/**
 * `asPath` with its `sortBy` set to `sort`, or removed for Best match. `page`
 * goes too: page 3 of one order is not page 3 of another.
 */
export function withListingSort(asPath: string, sort: ListingSort | undefined): string {
  const [beforeHash, hash] = asPath.split('#', 2);
  const [path, search = ''] = beforeHash.split('?', 2);
  const params = new URLSearchParams(search);
  params.delete('page');
  if (sort) params.set('sortBy', sort);
  else params.delete('sortBy');
  const qs = params.toString();
  return `${path}${qs ? `?${qs}` : ''}${hash ? `#${hash}` : ''}`;
}
